/**
 * React UI shell — textbox, choices, backlog, saves, settings.
 * Phaser owns the stage behind this; these are DOM layers on top, which keeps
 * text crisp, selectable, and accessible (screen readers, OS font scaling).
 *
 * Screen order is deliberate: the photosensitivity notice precedes anything that
 * animates, because a warning shown after the title has already flickered is not
 * a warning.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import type { Runtime, RuntimeEvent, HistoryEntry } from '@hon/core';
import type { Catalog, PackManifest } from '@hon/schema';
import { loadSave } from '@hon/core';
import { Title } from './Title.js';
import { Library, type BookCard } from './Library.js';
import { Shop } from './Shop.js';
import { SettingsScreen } from './SettingsScreen.js';
import { Warning } from './Warning.js';
import { Welcome } from './Welcome.js';
import { EpisodeEnd } from './EpisodeEnd.js';
import { Profile } from './Profile.js';
import { NameEntry } from './NameEntry.js';
import { Loading } from './Loading.js';
import { LanguagePicker } from './LanguagePicker.js';
import { CPS, load as loadPrefs, save as savePrefs, type Prefs } from './prefs.js';
import { loadWallet } from './wallet.js';

type Screen = 'welcome' | 'warning' | 'library' | 'loading' | 'title' | 'game' | 'shop' | 'profile' | 'settings';

const SAVE_KEY = 'hon.save';
/** A line never takes longer than this to reveal, regardless of speed setting. */
const MAX_REVEAL_MS = 6000;

/**
 * No economy exists: no wallet, no balance, no purchase sheet, no IAP.
 *
 * While this is false the UI must not render a price on anything, for the same
 * reason the tea/diamond counters were removed — a store reviewer treats
 * non-functional commerce UI as misrepresentation (App Store 2.1/4.2, Play
 * misrepresentation policy), and cannot distinguish "placeholder" from "broken".
 *
 * The pack schema keeps `cost`/`premium` so content authored now stays valid.
 * Flipping this to true is not sufficient on its own — it needs a wallet, a
 * balance check, and a real purchase flow behind it.
 *
 * One deliberate exception (client decision, 2026-08-09): the library's top
 * bar shows ticket/coin BALANCES from the real persisted wallet in
 * ui/wallet.ts. Balances of a working wallet are not commerce UI; prices,
 * "+" buttons, and a shop remain gated on this flag.
 */
const ECONOMY_ENABLED = false;

interface Line {
  name?: string | undefined; color: string; text: string;
  /** Which of four heights the frame sits at — see Say.at. */
  at?: 'upper' | 'middle' | 'lower' | 'bottom' | undefined;
}
/**
 * Which of the board's two option sizes a set of options wants.
 *
 * Measured against the longest one, because they all share a size: a set where
 * one option wraps and the others do not would otherwise be set in two.
 */
function fitClass(options: ReadonlyArray<{ text: string }>): string {
  const longest = options.reduce((n, o) => Math.max(n, o.text.length), 0);
  if (longest <= 24) return 'is-short';
  if (longest <= 48) return 'is-medium';
  return 'is-long';
}

interface ChoiceOpt { text: string; index: number; premium: boolean; diamonds: number }
interface ChoiceState { prompt?: string | undefined; promptImage?: string | undefined; options: ChoiceOpt[] }

const STATUS_LABEL: Record<'ru' | 'en', Record<string, string | undefined>> = {
  ru: { 'coming-soon': 'выходит' },
  en: { 'coming-soon': 'coming soon' },
};

/** Catalog language. The catalog is bilingual; episode SCRIPTS are Russian
    only, so this switches the library, not the story. */
const LANG_KEY = 'hon.lang';

export function App({ runtime, pack, baseUrl, catalog }: { runtime: Runtime; pack: PackManifest; baseUrl: string; catalog: Catalog }) {
  const [lang, setLang] = useState<'ru' | 'en'>(() =>
    localStorage.getItem(LANG_KEY) === 'en' ? 'en' : 'ru',
  );
  const toggleLang = useCallback(() => {
    setLang((l) => {
      const next = l === 'ru' ? 'en' : 'ru';
      localStorage.setItem(LANG_KEY, next);
      return next;
    });
  }, []);

  // Books without cover art can't be shown in a cover-driven library.
  const books: BookCard[] = catalog.books
    .filter((b) => b.cover)
    .map((b) => ({
      id: b.id,
      title: b.title[lang],
      genres: b.genres[lang],
      synopsis: b.synopsis[lang],
      cover: `${baseUrl}/${b.cover}`,
      motion: b.motion ? `${baseUrl}/${b.motion}` : undefined,
      status: STATUS_LABEL[lang][b.status],
      playable: b.packId !== null,
    }));

  const [prefs, setPrefs] = useState<Prefs>(() => loadPrefs());
  /* Real balances, shared with the library's top bar — see ui/wallet.ts. */
  const [wallet] = useState(loadWallet);
  const [screen, setScreen] = useState<Screen>('welcome');

  /* Which shop tab to land on — the pills' "+" buttons deep-link to theirs. */
  const [shopTab, setShopTab] = useState<'tickets' | 'coins'>('tickets');
  /* The globe opens a sheet over the current screen, not a separate route. */
  const [langOpen, setLangOpen] = useState(false);

  const [line, setLine] = useState<Line | null>(null);
  const [choice, setChoice] = useState<ChoiceState | null>(null);
  /* Once the episode is warm it stays warm; the loader is not shown again. */
  const [loaded, setLoaded] = useState(false);
  const [card, setCard] = useState<{ title?: string | undefined; subtitle?: string | undefined } | null>(null);
  const [naming, setNaming] = useState<
    { key: string; prompt: string; note?: string | undefined; value: string; maxLength: number } | null
  >(null);
  const [typed, setTyped] = useState('');
  const [done, setDone] = useState(true);
  const [backlog, setBacklog] = useState(false);
  const [ended, setEnded] = useState<{ next: { book: string; episode: string } | undefined } | null>(null);
  const started = screen === 'game';
  const historyRef = useRef<HistoryEntry[]>(runtime.history);
  const advanceRef = useRef<HTMLButtonElement>(null);
  /*
   * The running reveal, so a tap can stop it. Without this handle, tapping to
   * skip set the full line and then the interval's next tick overwrote it with
   * a shorter prefix and carried on typing — the line snapped to the end, rewound
   * and started again, and there was no way to skip anything.
   */
  const revealRef = useRef<number | null>(null);

  const updatePrefs = useCallback((next: Prefs) => setPrefs(savePrefs(next)), []);

  useEffect(() => {
    const off = runtime.on((e: RuntimeEvent) => {
      if (e.type === 'say') {
        setChoice(null);
        setLine({ name: e.name, color: e.color, text: e.text, at: e.at });
        setTyped('');
        setDone(false);
      } else if (e.type === 'card') {
        setLine(null);
        setCard({ title: e.title, subtitle: e.subtitle });
      } else if (e.type === 'input') {
        setChoice(null);
        setNaming({ key: e.key, prompt: e.prompt, note: e.note, value: e.value, maxLength: e.maxLength });
      } else if (e.type === 'choice') {
        setChoice({
          prompt: e.prompt,
          promptImage: e.promptImage,
          options: e.options.map((o) => ({ text: o.text, index: o.index, premium: o.premium, diamonds: o.diamonds })),
        });
      } else if (e.type === 'end') {
        setEnded({ next: e.next });
      }
    });
    return off;
  }, [runtime]);

  // Held back until the title screen is dismissed, so the first line isn't
  // already on screen behind it.
  useEffect(() => {
    if (started) runtime.advance();
  }, [started, runtime]);

  const stopReveal = useCallback(() => {
    if (revealRef.current === null) return;
    window.clearInterval(revealRef.current);
    revealRef.current = null;
  }, []);

  // Typewriter. Speed comes from prefs; the clamp keeps a very long line from
  // taking half a minute at the slow setting.
  useEffect(() => {
    if (!line) return;
    const cps = CPS[prefs.textSpeed];
    if (!Number.isFinite(cps)) { setTyped(line.text); setDone(true); return; }

    const perChar = Math.max(8, Math.min(1000 / cps, MAX_REVEAL_MS / Math.max(1, line.text.length)));
    let i = 0;
    const id = window.setInterval(() => {
      i++;
      setTyped(line.text.slice(0, i));
      if (i >= line.text.length) { stopReveal(); setDone(true); }
    }, perChar);
    revealRef.current = id;
    return stopReveal;
  }, [line, prefs.textSpeed, stopReveal]);

  const advance = useCallback(() => {
    if (!started || choice || naming || ended || backlog) return;
    if (card) { setCard(null); runtime.advance(); return; }
    // First press completes the line — which means stopping the reveal, not just
    // outrunning it.
    if (!done && line) { stopReveal(); setTyped(line.text); setDone(true); return; }
    runtime.advance();
  }, [started, choice, naming, card, done, ended, backlog, line, runtime, stopReveal]);

  useEffect(() => {
    if (!started) return;
    const onKey = (e: KeyboardEvent) => {
      // Space/Enter are handled natively by the focused advance button; only
      // intercept when focus is elsewhere, so we never double-fire.
      if ((e.key === 'Enter' || e.key === ' ') && document.activeElement !== advanceRef.current) {
        e.preventDefault();
        advance();
      }
      if (e.key === 'Escape') { e.preventDefault(); setBacklog((b) => !b); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, started]);

  const save = () => {
    localStorage.setItem(SAVE_KEY, JSON.stringify(runtime.save(Date.now())));
  };

  const load = () => {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return;
    try {
      // loadSave upgrades older formats; a corrupt save must not kill the session.
      runtime.restore(loadSave(JSON.parse(raw)), Date.now());
    } catch (e) {
      console.warn('[hon] could not load save:', e);
      return;
    }
    setEnded(null);
    setChoice(null);
    runtime.advance();
  };

  /**
   * Start — or restart — the episode the title card is showing.
   *
   * Reaching the end leaves the runtime's frame stack empty, so simply going
   * back to `game` had it emit `end` again the moment the stage mounted: the
   * episode was unplayable a second time and the end screen came straight back.
   * Nothing reset it, because nothing ever needed to until there was an ending
   * to come back from.
   */
  const startEpisode = useCallback(() => {
    runtime.playEpisode(runtime.book.id, runtime.episode.id);
    setEnded(null);
    setChoice(null);
    setNaming(null);
    setCard(null);
    setLine(null);
    setTyped('');
    setDone(true);
    setScreen('game');
  }, [runtime]);

  const playNext = () => {
    if (!ended?.next) return;
    runtime.playEpisode(ended.next.book, ended.next.episode);
    setEnded(null);
    setChoice(null);
    setLine(null);
    runtime.advance();
  };

  const settingsScreen = (
    <SettingsScreen
      prefs={prefs}
      onChange={updatePrefs}
      onClose={() => setScreen('library')}
      lang={lang}
      onLang={() => setLangOpen(true)}
      onShop={() => { setShopTab('coins'); setScreen('shop'); }}
      onProfile={() => setScreen('profile')}
    />
  );

  /* --- screens ----------------------------------------------------------- */

  const langSheet = langOpen && (
    <LanguagePicker
      lang={lang}
      onPick={(next) => { if (next !== lang) toggleLang(); }}
      onClose={() => setLangOpen(false)}
    />
  );

  if (screen === 'welcome') {
    // The photosensitivity notice must still precede anything that animates,
    // so the splash hands off to it rather than straight to the library.
    return <Welcome onDone={() => setScreen(prefs.seenWarning ? 'library' : 'warning')} />;
  }

  if (screen === 'warning') {
    return (
      <Warning
        onChoose={(full) => {
          updatePrefs({ ...prefs, seenWarning: true, effects: full ? 'full' : 'reduced' });
          setScreen('library');
        }}
      />
    );
  }

  if (screen === 'library') {
    return (
      <>
        <Library
          books={books}
          lang={lang}
          onLang={() => setLangOpen(true)}
          onOpen={(id) => {
            // Only ВЫСОКАЯ КУХНЯ has content; the rest are cover art only.
            /* Everything the episode can reach is fetched and decoded before
               the title card, so nothing arrives mid-sentence. */
            if (books.find((b) => b.id === id)?.playable) setScreen(loaded ? 'title' : 'loading');
          }}
          onShop={(tab) => { setShopTab(tab ?? 'tickets'); setScreen('shop'); }}
          onSettings={() => setScreen('settings')}
          onProfile={() => setScreen('profile')}
        />
        {langSheet}
      </>
    );
  }

  if (screen === 'settings') return <>{settingsScreen}{langSheet}</>;

  if (screen === 'profile') {
    return (
      <>
        <Profile
          catalog={catalog}
          baseUrl={baseUrl}
          lang={lang}
          onBack={() => setScreen('library')}
          onLang={() => setLangOpen(true)}
          onShop={() => { setShopTab('coins'); setScreen('shop'); }}
          onSettings={() => setScreen('settings')}
          tickets={wallet.tickets}
          coins={wallet.coins}
        />
        {langSheet}
      </>
    );
  }

  if (screen === 'shop') {
    return (
      <>
        <Shop
          lang={lang}
          initialTab={shopTab}
          onBack={() => setScreen('library')}
          onSettings={() => setScreen('settings')}
          onProfile={() => setScreen('profile')}
          onLang={() => setLangOpen(true)}
          tickets={wallet.tickets}
          coins={wallet.coins}
        />
        {langSheet}
      </>
    );
  }

  if (screen === 'loading') {
    return (
      <Loading
        pack={pack}
        baseUrl={baseUrl}
        onDone={() => { setLoaded(true); setScreen('title'); }}
      />
    );
  }

  if (screen === 'title') {
    return (
      <>
        <Title
          onStart={startEpisode}
          onBack={() => setScreen('library')}
          number={runtime.episode.number}
          title={runtime.episode.title}
        />
      </>
    );
  }

  return (
    <div className="ui-root">
      {/* The whole stage is the advance affordance. A real <button> gives it a
          keyboard path, a name, and click-cancellation (WCAG 2.5.2) for free. */}
      <button
        ref={advanceRef}
        className="advance"
        onClick={advance}
        disabled={!!choice || !!ended}
        aria-label={done ? 'Далее' : 'Показать строку целиком'}
      />

      <div className="crt crt-diegetic" aria-hidden="true" />

      <div className="hud">
        <button onClick={() => setBacklog((b) => !b)} aria-expanded={backlog}>Журнал</button>
        <button onClick={save}>Сохранить</button>
        <button onClick={load}>Загрузить</button>
        <button onClick={() => setScreen('settings')} aria-label="Настройки">⚙</button>
      </div>

      {backlog && (
        <div className="backlog" role="dialog" aria-modal="true" aria-label="Журнал">
          <h2>Журнал</h2>
          {historyRef.current.length === 0 && <p className="backlog-empty">Пока пусто.</p>}
          {historyRef.current.map((h, i) => (
            <p key={i}><strong>{h.name ?? ''}</strong> {h.text}</p>
          ))}
          <button onClick={() => setBacklog(false)}>Закрыть</button>
        </div>
      )}

      {/*
        * The box does not go away when a choice comes up. Figma draws the line
        * that prompted the choice still sitting in its frame with the plates
        * stacked over its lower half — replacing one with the other loses the
        * line you are answering.
        */}
      {choice && (
        <div
          /*
           * Marina sizes option text to the option: «Вылить в раковину» is set
           * at 17px on frame 14, and frame 85's longer three at 12px. The
           * plate is a fixed painting and does not grow, so the type has to be
           * what gives — at one size the writer's longest option ran to three
           * lines and out through the top and bottom of the plate.
           */
          className={`choices ${fitClass(choice.options)}`}
          role="group"
          aria-label={choice.prompt ?? 'Выбор'}
        >
          {/*
            * Two shapes of prompt. «Красное Мадиран» names an object and gets
            * the glass card with the object painted on it; «ВЫБЕРИТЕ ОДЕЖДУ
            * ГЛАВНОЙ ГЕРОИНЕ» is an instruction and gets a heading at the top
            * of the board. The card is what the art asks for, not what every
            * prompt gets — on the wardrobe screen it put a pane of frosted
            * glass over the outfit the player is choosing between.
            */}
          {choice.prompt && (choice.promptImage ? (
            <div className="prompt-card">
              <p className="prompt">{choice.prompt}</p>
              <span
                className="prompt-art"
                style={{ backgroundImage: `url(${baseUrl}/${choice.promptImage})` }}
                aria-hidden="true"
              />
            </div>
          ) : (
            <p className="prompt is-heading">{choice.prompt}</p>
          ))}
          {choice.options.map((o) => (
            <button
              key={o.index}
              className={ECONOMY_ENABLED && o.premium ? 'premium' : undefined}
              onClick={() => { setChoice(null); runtime.choose(o.index); }}
            >
              {o.text}
              {ECONOMY_ENABLED && o.diamonds > 0 && <span className="cost">&#128142; {o.diamonds}</span>}
            </button>
          ))}
        </div>
      )}
      {card && (
        <div className="card-beat" role="note">
          {card.title && <h2 className="card-title">{card.title}</h2>}
          <div className="card-rule" aria-hidden="true" />
          {card.subtitle && <p className="card-sub">{card.subtitle}</p>}
        </div>
      )}
      {naming && (
        <NameEntry
          prompt={naming.prompt}
          note={naming.note}
          value={naming.value}
          maxLength={naming.maxLength}
          onSubmit={(name) => { setNaming(null); runtime.submit(name); }}
        />
      )}
      {line && !choice?.prompt && !naming && !card ? (
        /* Narration is centred in the frame and carries no name; a character's
           line is ranged left under theirs. Both come from the design. */
        <div
          /*
           * One frame, one size, four heights. The board moves it to stay off
           * whoever is talking; it does not resize it, whatever the dragged
           * rectangles say.
           */
          className={[
            'dialogue',
            line.name ? '' : 'is-narration',
            `at-${line.at ?? 'upper'}`,
          ].filter(Boolean).join(' ')}
        >
          {/*
            * The plate is on every frame, named or not. Narration keeps it and
            * fills it with «...» — that is what «Vector 9» carries in frame 1,
            * and dropping the plate for unnamed lines made the box change shape
            * every time the story stepped out of a character's mouth.
            */}
          <div className="speaker">{line.name ?? '. . .'}</div>
          <div className="textbox">
            {/* Announced as one unit once revealed, not character by character. */}
            <p className="text" aria-live="polite" aria-atomic="true">
              {done ? (line.name ? `${line.name}: ${line.text}` : line.text) : ''}
            </p>
            <p className="text text-visual" aria-hidden="true">
              {typed}{!done && <span className="caret" />}
            </p>
          </div>
        </div>
      ) : null}

      {ended && (
        <EpisodeEnd
          vars={runtime.vars}
          pack={pack}
          hasNext={!!ended.next}
          onContinue={playNext}
          onLibrary={() => { setEnded(null); setScreen('library'); }}
        />
      )}
    </div>
  );
}
